import { useCallback, useEffect, useRef, useState } from 'react';

import { WindowSize } from 'hooks/useResize';
import {
  canvasService,
  MAX_DRAW_CANVAS_DIMENSION,
  rotationService,
  signedRequestService,
} from 'services/fileService';

import { getDimensions } from '../model';
import { DrawProps } from '../types';

interface UseImageSourceParams {
  file: DrawProps['file'];
  drawing: DrawProps['drawing'];
  cleared: boolean;
  container: HTMLElement;
  windowSize: WindowSize;
}

interface UseImageSourceResult {
  src: string | undefined;
  dimensions: WindowSize;
  imageNaturalDimensions: WindowSize;
}

const EMPTY_SIZE: WindowSize = { width: 0, height: 0 };

export default function useImageSource({
  file,
  drawing,
  cleared,
  container,
  windowSize,
}: UseImageSourceParams): UseImageSourceResult {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [dimensions, setDimensions] = useState<WindowSize>(EMPTY_SIZE);
  const [imageNaturalDimensions, setImageNaturalDimensions] =
    useState<WindowSize>(EMPTY_SIZE);

  const rotatedSrcCleanupRef = useRef<(() => void) | null>(null);
  const cappedDisplayCleanupRef = useRef<(() => void) | null>(null);

  const releaseRotatedSrc = useCallback(() => {
    if (rotatedSrcCleanupRef.current) {
      rotatedSrcCleanupRef.current();
      rotatedSrcCleanupRef.current = null;
    }
  }, []);

  const releaseCappedDisplaySrc = useCallback(() => {
    if (cappedDisplayCleanupRef.current) {
      cappedDisplayCleanupRef.current();
      cappedDisplayCleanupRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      releaseRotatedSrc();
      releaseCappedDisplaySrc();
    };
  }, [releaseRotatedSrc, releaseCappedDisplaySrc]);

  useEffect(() => {
    let cancelled = false;

    if (!container) {
      return;
    }

    const containerDimensions = getDimensions(container);
    setDimensions(containerDimensions);

    if (!src) {
      return;
    }

    canvasService
      .loadImageFromSource(src)
      .then((image) => {
        if (cancelled) {
          canvasService.releaseImageElement(image);
          return;
        }

        const naturalDimensions = {
          width: image.width,
          height: image.height,
        };
        setImageNaturalDimensions(naturalDimensions);

        const largestSide = Math.max(image.width, image.height);
        if (largestSide <= MAX_DRAW_CANVAS_DIMENSION) {
          canvasService.releaseImageElement(image);
          return;
        }

        return canvasService
          .prepareDrawDisplaySource(
            src,
            image.width,
            image.height,
            MAX_DRAW_CANVAS_DIMENSION,
            image
          )
          .then((displaySource) => {
            if (cancelled) {
              displaySource.cleanup?.();
              return;
            }

            releaseCappedDisplaySrc();
            cappedDisplayCleanupRef.current = displaySource.cleanup ?? null;
            setSrc(displaySource.source);
          });
      })
      .catch(() => {
        if (cancelled) return;
        setImageNaturalDimensions(containerDimensions);
      });

    return () => {
      cancelled = true;
    };
  }, [container, src, windowSize.width, windowSize.height, releaseCappedDisplaySrc]);

  useEffect(() => {
    let cancelled = false;

    const nextSrc = signedRequestService.resolveDrawInitialSource(
      file,
      drawing,
      Boolean(cleared)
    );
    if (!nextSrc) {
      releaseRotatedSrc();
      releaseCappedDisplaySrc();
      setSrc(undefined);
      setImageNaturalDimensions(EMPTY_SIZE);
      return;
    }

    rotationService
      .applyImageRotationToSource(nextSrc, file.imageRotation)
      .then(({ source, cleanup }) => {
        if (cancelled) {
          cleanup?.();
          return;
        }
        releaseRotatedSrc();
        releaseCappedDisplaySrc();
        rotatedSrcCleanupRef.current = cleanup ?? null;
        setImageNaturalDimensions(EMPTY_SIZE);
        setSrc(source);
      })
      .catch(() => {
        if (cancelled) return;
        releaseRotatedSrc();
        releaseCappedDisplaySrc();
        setImageNaturalDimensions(EMPTY_SIZE);
        setSrc(nextSrc);
      });

    return () => {
      cancelled = true;
    };
  }, [file, drawing, cleared, releaseRotatedSrc, releaseCappedDisplaySrc]);

  return { src, dimensions, imageNaturalDimensions };
}
